import { OperationVariables } from '@apollo/client'
import * as Sentry from '@sentry/core'

import { $RootStore } from '../../src/stores/RootStore'
import { BOOKING_REMINDER_NOT_SENT, FormattedError, formatError, SALON_MEMBER_HAS_UPCOMING_BOOKINGS } from './ErrorHelper'

const errorCodeLogLevels: Record<string, Sentry.SeverityLevel> = {
  'PROMOCODE_NOT_FOUND': 'debug',
  'EXISTING_APPLICATION': 'log',
  [SALON_MEMBER_HAS_UPCOMING_BOOKINGS]: 'log',
  [BOOKING_REMINDER_NOT_SENT]: 'log',
}

// Rethrown error keeps API's code so a caller can tell its own failures apart.
export type RequestError = Error & { code: string }

export const requestErrorCode = (e: unknown): string | undefined =>
  (e as Partial<RequestError> | undefined)?.code

interface UserContextData {
  _id?: Maybe<string>
  emailAddress?: Maybe<string>
  firstName?: Maybe<string>
  lastName?: Maybe<string>
}

export default abstract class BaseErrorHandler {
  store: $RootStore

  constructor(store: $RootStore) {
    this.store = store
  }

  manageRawError = (e: any, queryName = '', queryVariables: OperationVariables = {}) => {
    const { config, communication } = this.store

    const formattedError = formatError(e, queryName, queryVariables, this.location, this.userId)
    const { code } = formattedError

    if (config.name === 'development') {
      console.info(`ERROR: ${formattedError.code} - ${formattedError.queryName} - ${formattedError.message}`)
      console.info('More error info: ', formattedError)
    }

    if (this.expiredCodes.includes(code)) this.handleExpiredSession()
    else this.showErrorInUI(formattedError)

    const logLevel: Sentry.SeverityLevel = errorCodeLogLevels[code] || 'error'
    if (logLevel === 'debug') return

    const fingerprint = [code]

    if (code === 'INTERNAL_SERVER_ERROR' && formattedError.message) {
      fingerprint.push(formattedError.message)
    }

    Sentry.captureMessage(code, {
      fingerprint,
      level: logLevel,
      user: {
        id: this.userId,
        email: this.userEmail,
      },
      extra: formattedError as Record<string, any>,
    })

    this.trackInAnalytics(formattedError)

    communication.latestErrorCode = code

    const error = new Error(formattedError.message) as RequestError
    error.code = code
    e.message = 'RequestError'

    throw error
  }

  setContext(contextName: string, o: Record<string, any>) {
    Sentry.setContext(contextName, o)
  }

  setUserContext = (user: UserContextData) => {
    Sentry.setUser({
      id: user._id || '',
      email: user.emailAddress || '',
      username: `${user.firstName} ${user.lastName}`,
    })
  }

  /*
   * Capture custom exceptions$
   * Call this from any catch block you want to track in Sentry
   */
  captureException(error: any, extra?: Record<string, any>) {
    Sentry.captureException(error, {
      fingerprint: [error],
      extra,
      user: {
        id: this.userId,
        email: this.userEmail,
      },
    })

    if (this.store.config.name === 'development') {
      console.error(error)
    }
  }

  abstract handleExpiredSession(): void
  abstract showErrorInUI(error: FormattedError): void
  abstract trackInAnalytics(error: FormattedError): void
  abstract get location(): string
  abstract get userId(): string
  abstract get userEmail(): string
  abstract expiredCodes: string[]
}

